import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Car, Calendar, Plus } from '@/ui/icons'
import { Button } from '@/ui/components/'
import { Card, CardContent } from '@/ui/components/'
import { PageLayout } from '@/ui/layout'
import { ProfileSkeleton, Skeleton } from '@/ui/components'
import { useAuth } from '@/modules/auth'
import { UserProfileApi, UserProfile as UserProfileType } from '@/shared/api/users'
import { 
  UserProfileHeader,
  UserStatsSection,
  ContributionGraph,
  PreferencesSection
} from '@/modules/users/components'
import { useUserContributions } from '@/modules/users/hooks/useUserContributions'
import { VehicleApi, VehicleProfile } from '@/shared/api/vehicles'
import { 
  VehicleSpecs,
  VehicleModificationsProfile 
} from '@/modules/vehicles/components'
import { VehicleEditModal } from '@/modules/vehicles/components/VehicleEditModal'
import { useDashboard } from '@/modules/dashboard'

export default function UserProfile() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { user, getAccessToken } = useAuth()
  const { dashboardData } = useDashboard()

  const [userProfile, setUserProfile] = useState<UserProfileType | null>(null)
  const [vehicles, setVehicles] = useState<VehicleProfile[]>([])
  const [primaryVehicle, setPrimaryVehicle] = useState<VehicleProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [isVehicleEditOpen, setIsVehicleEditOpen] = useState(false)
  const [vehicleBeingEdited, setVehicleBeingEdited] = useState<VehicleProfile | null>(null)

  const isOwnProfile = user?.id === userId

  // Get contributions - MUST be called before any early returns (Rules of Hooks)
  const { contributions, isLoading: isLoadingContributions } = useUserContributions(
    userId || ''
  )

  useEffect(() => {
    if (!userId) return

    const loadProfileData = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const userProfileApi = new UserProfileApi(getAccessToken)
        const vehicleApi = new VehicleApi(getAccessToken)

        // Load user profile
        const profile = await userProfileApi.getUserProfile(userId)
        setUserProfile(profile)

        // Load vehicles if viewing own profile
        if (isOwnProfile) {
          const vehicleData = await vehicleApi.getVehicles()
          setVehicles(vehicleData)
          const primary = vehicleData.find(v => v.is_primary) || vehicleData[0] || null
          setPrimaryVehicle(primary)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profile')
      } finally {
        setIsLoading(false)
      }
    }

    loadProfileData()
  }, [userId, isOwnProfile, getAccessToken])

  const handleProfileUpdate = (updatedProfile: UserProfileType) => {
    setUserProfile(updatedProfile)
  }

  const handleAddVehicle = () => {
    navigate('/onboarding', { state: { fromVehicleManagement: true } })
  }

  const handleOpenVehicleEdit = (v: VehicleProfile) => {
    setVehicleBeingEdited(v)
    setIsVehicleEditOpen(true)
  }

  const handleVehicleUpdated = (updated: VehicleProfile) => {
    // Update vehicles array and primaryVehicle if needed
    setVehicles(prev => {
      const next = prev.map(v => (v.id === updated.id ? updated : v))
      return next
    })
    setPrimaryVehicle(prev => (prev && prev.id === updated.id ? updated : prev))
  }

  if (isLoading) {
    return <ProfileSkeleton />
  }

  if (error || !userProfile) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Profile</h2>
            <p className="text-gray-600 mb-4">{error || 'Profile not found'}</p>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        </div>
      </PageLayout>
    )
  }

  // Get user name from auth metadata
  const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'

  // Get recent trips for display (only for own profile)
  const recentTrips = isOwnProfile ? (dashboardData?.recentTrips?.slice(0, 3) || []) : []

  // Get modifications count
  const modificationsCount = 0 // TODO: Get from modifications hook if needed

  return (
    <PageLayout>
      <div className="min-h-screen bg-earth-50">
        {/* User Profile Header */}
        <div className="bg-earth-50 border-b border-gray-200">
          <UserProfileHeader
            profile={userProfile}
            userName={userName}
            isOwnProfile={isOwnProfile}
            onProfileUpdate={handleProfileUpdate}
          />
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Contribution Graph - Only show for own profile */}
          {isOwnProfile && (
            <Card className="mb-8">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-foreground mb-4">
                  Contributions
                </h2>
                {isLoadingContributions ? (
                  <div className="py-4 space-y-3">
                    <Skeleton className="h-8 w-full rounded-md" />
                    <Skeleton className="h-32 w-full rounded-md" />
                  </div>
                ) : (
                  <ContributionGraph contributions={contributions} />
                )}
              </CardContent>
            </Card>
          )}

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-8 mb-8">
            {/* Left Column - Vehicle Info (only for own profile with vehicles) */}
            {isOwnProfile && primaryVehicle && (
              <div className="lg:col-span-1 space-y-6">
                <VehicleSpecs 
                  vehicle={primaryVehicle}
                  modificationsCount={modificationsCount}
                  onEdit={isOwnProfile ? handleOpenVehicleEdit : undefined}
                />
                <VehicleModificationsProfile vehicleId={primaryVehicle.id} />
              </div>
            )}

            {/* Right Column - Stats */}
            <div className={`${isOwnProfile && primaryVehicle ? 'lg:col-span-2' : 'lg:col-span-3'} space-y-6`}>
              <UserStatsSection userId={userId!} />

              {/* Preferences - Only for own profile */}
              {isOwnProfile && <PreferencesSection />}

              {/* Recent Trips - Only for own profile */}
              {isOwnProfile && recentTrips.length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-foreground mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-adventure-600 dark:text-adventure-400" />
                      Recent Trips
                    </h2>
                    <div className="space-y-3">
                      {recentTrips.map((trip) => (
                        <Link
                          key={trip.id}
                          to={`/trips/${trip.id}`}
                          className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 dark:bg-earth-100 dark:hover:bg-earth-200 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-medium text-gray-900 dark:text-earth-800">{trip.name}</h3>
                              <p className="text-sm text-gray-600 dark:text-earth-600 mt-1">
                                {trip.start_date && new Date(trip.start_date).toLocaleDateString()}
                                {trip.end_date && ` - ${new Date(trip.end_date).toLocaleDateString()}`}
                              </p>
                            </div>
                            <div className="text-sm text-gray-500 dark:text-earth-600">
                              {trip.member_count || 0} member{(trip.member_count || 0) !== 1 ? 's' : ''}
                            </div>
                          </div>
                        </Link>
                      ))}
                      <div className="pt-2">
                        <Button variant="outline" asChild className="w-full">
                          <Link to="/trips">View All Trips</Link>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Add Vehicle CTA - Only for own profile without vehicles */}
              {isOwnProfile && vehicles.length === 0 && (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Vehicle Added</h3>
                    <p className="text-gray-600 mb-6">
                      Add your vehicle to showcase it on your profile.
                    </p>
                    <Button 
                      className="flex items-center space-x-2 mx-auto"
                      onClick={handleAddVehicle}
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Your First Vehicle</span>
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>

        {isOwnProfile && vehicleBeingEdited && (
          <VehicleEditModal
            isOpen={isVehicleEditOpen}
            onClose={() => setIsVehicleEditOpen(false)}
            vehicle={vehicleBeingEdited}
            onVehicleUpdated={handleVehicleUpdated}
          />
        )}
      </div>
    </PageLayout>
  )
}

